import React, { useState, useEffect } from 'react';
import { FiFileText, FiDownload, FiCheckCircle, FiAlertTriangle, FiClock } from 'react-icons/fi';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useWholesaleAuth } from '../../contexts/WholesaleAuthContext';
import styles from './InvoicesAndPayments.module.css';

const StatusBadge = ({ status }) => {
  const statusMap = {
    paid: { icon: <FiCheckCircle />, className: styles.paid },
    due: { icon: <FiClock />, className: styles.due },
    overdue: { icon: <FiAlertTriangle />, className: styles.overdue },
    pending: { icon: <FiFileText />, className: styles.due },
  };
  const currentStatus = statusMap[status?.toLowerCase()] || statusMap.pending;
  return (
    <div className={`${styles.statusBadge} ${currentStatus.className}`}>
      {currentStatus.icon}
      <span>{status || 'Pending'}</span>
    </div>
  );
};

const WholesaleInvoicesAndPayments = () => {
  const { wholesaleCustomer } = useWholesaleAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialSummary, setFinancialSummary] = useState({
    totalOutstanding: 0,
    availableCredit: 0,
    creditLimit: 0,
    nextPaymentDue: null,
    nextPaymentAmount: 0,
    loading: true
  });

  useEffect(() => {
    const fetchInvoicesAndPayments = async () => {
      if (!wholesaleCustomer?.id) return;

      try {
        setLoading(true);
        
        // Fetch invoices from Firebase
        const invoicesRef = collection(db, 'wholesaleInvoices');
        const invoicesQuery = query(
          invoicesRef,
          where('customerId', '==', wholesaleCustomer.id),
          orderBy('createdAt', 'desc')
        );
        
        const invoicesSnapshot = await getDocs(invoicesQuery);
        const invoicesList = invoicesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setInvoices(invoicesList);

        // Calculate financial summary
        const totalOutstanding = invoicesList
          .filter(invoice => invoice.status !== 'paid')
          .reduce((sum, invoice) => sum + (invoice.amount || 0), 0);

        const creditLimit = wholesaleCustomer.creditLimit || 10000;
        const creditUsed = wholesaleCustomer.creditUsed || 0;
        const availableCredit = creditLimit - creditUsed;

        // Find next payment due
        const overdueInvoices = invoicesList
          .filter(invoice => invoice.status === 'overdue' || invoice.status === 'due')
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        const nextPayment = overdueInvoices[0] || null;

        setFinancialSummary({
          totalOutstanding,
          availableCredit,
          creditLimit,
          nextPaymentDue: nextPayment?.dueDate || null,
          nextPaymentAmount: nextPayment?.amount || 0,
          loading: false
        });

        setError(null);
      } catch (err) {
        console.error('Error fetching invoices:', err);
        setError('Could not load invoices and payment information.');
        setFinancialSummary(prev => ({ ...prev, loading: false }));
      } finally {
        setLoading(false);
      }
    };

    fetchInvoicesAndPayments();
  }, [wholesaleCustomer?.id]);

  const formatDate = (dateField) => {
    if (!dateField) return 'N/A';
    
    if (dateField.toDate) {
      return dateField.toDate().toLocaleDateString();
    }
    
    return new Date(dateField).toLocaleDateString();
  };

  const generateInvoiceNumber = (invoice) => {
    if (invoice.invoiceNumber) return invoice.invoiceNumber;
    if (invoice.id) return `INV-${invoice.id.slice(0, 8)}`;
    return 'N/A';
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>Invoices & Payments</div>
          <div className={styles.headerSubtitle}>Review your account balance and download past invoices.</div>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Loading financial information...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>Invoices & Payments</div>
          <div className={styles.headerSubtitle}>Review your account balance and download past invoices.</div>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', background: '#f9f9f9', border: '1px solid #eee' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>Invoices & Payments</div>
        <div className={styles.headerSubtitle}>Review your account balance and download past invoices.</div>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.cardTitle}>Total Outstanding</div>
          <div className={styles.cardValue}>
            {financialSummary.loading ? 'Loading...' : `QAR ${financialSummary.totalOutstanding.toFixed(2)}`}
          </div>
          <div className={styles.cardSubtitle}>
            {financialSummary.loading ? '' : `Across ${invoices.filter(inv => inv.status !== 'paid').length} invoices`}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardTitle}>Available Credit</div>
          <div className={styles.cardValue}>
            {financialSummary.loading ? 'Loading...' : `QAR ${financialSummary.availableCredit.toFixed(2)}`}
          </div>
          <div className={styles.cardSubtitle}>
            {financialSummary.loading ? '' : `Credit Limit: QAR ${financialSummary.creditLimit.toFixed(2)}`}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardTitle}>Next Payment Due</div>
          <div className={styles.cardValue}>
            {financialSummary.loading ? 'Loading...' : 
             financialSummary.nextPaymentAmount > 0 ? `QAR ${financialSummary.nextPaymentAmount.toFixed(2)}` : 'No payments due'}
          </div>
          <div className={styles.cardSubtitle}>
            {financialSummary.loading ? '' : 
             financialSummary.nextPaymentDue ? `Due ${formatDate(financialSummary.nextPaymentDue)}` : 'All payments up to date'}
          </div>
        </div>
      </div>

      <div className={styles.invoiceList}>
        <div className={styles.listHeader}>
          <div>Invoice ID</div>
          <div>Date</div>
          <div>Due Date</div>
          <div>Amount</div>
          <div>Status</div>
          <div></div>
        </div>
        {invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            No invoices found
          </div>
        ) : (
          invoices.map((invoice) => (
            <div key={invoice.id} className={styles.invoiceRow}>
              <div className={styles.invoiceId}>{generateInvoiceNumber(invoice)}</div>
              <div>{formatDate(invoice.createdAt)}</div>
              <div>{formatDate(invoice.dueDate)}</div>
              <div className={styles.invoiceAmount}>QAR {(invoice.amount || 0).toFixed(2)}</div>
              <div><StatusBadge status={invoice.status} /></div>
              <div className={styles.invoiceActions}>
                <button className={styles.actionButton}>
                  <FiDownload />
                  <span>Download</span>
                </button>
                {invoice.status !== 'paid' && (
                  <button className={`${styles.actionButton} ${styles.payButton}`}>
                    Pay Now
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default WholesaleInvoicesAndPayments; 